import { formatPhone } from "@purlink/shared/phone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft,
  Building2,
  ChevronDown,
  ExternalLink,
  EyeOff,
  Megaphone,
  Pencil,
  Phone,
  Send,
  ShieldCheck,
  Tags,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Badge, StageDot, type StageColor } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardHeader, CardTitle } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { DatePicker } from "~/components/ui/date-picker";
import { Alert, EmptyState, Skeleton } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { toast } from "~/components/ui/toaster";
import { ActivityTimeline } from "~/features/leads/activity-timeline";
import {
  leadActions,
  leadDetailQuery,
  leadKeys,
  stagesQuery,
  type LeadDetail,
} from "~/features/leads/api";
import { useComposeEmail } from "~/features/emails/lazy-compose";
import { LeadEmailsPanel } from "~/features/emails/lead-emails-panel";
import { EmailFlagBadge, FollowUpBadge } from "~/features/leads/lead-badges";
import { AssignLeadDialog } from "~/features/leads/assign-dialog";
import { LeadFormDialog } from "~/features/leads/lead-form-dialog";
import { AssigneeAvatars } from "~/features/leads/lead-markers";
import { useLeadReview, useRefreshReviewState } from "~/features/leads/use-lead-review";
import { NotesPanel } from "~/features/leads/notes-panel";
import { ApiError } from "~/lib/api";
import { errorMessage, useCurrentUser } from "~/lib/auth";
import { channelLabel, formatDate, formatDateTime, fullName } from "~/lib/format";
import { useCan } from "~/lib/use-can";

export const Route = createFileRoute("/_app/leads/$leadId")({
  component: LeadPage,
});

function LeadPage() {
  const { leadId } = Route.useParams();
  const lead = useQuery(leadDetailQuery(leadId));

  if (lead.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (lead.isError) {
    const missing = lead.error instanceof ApiError && lead.error.status === 404;
    return (
      <Card>
        <EmptyState
          title={missing ? "This lead no longer exists" : "Couldn't load this lead"}
          description={missing ? "It may have been deleted." : errorMessage(lead.error)}
          action={
            <Button variant="secondary" asChild>
              <Link to="/leads">Back to All Leads</Link>
            </Button>
          }
        />
      </Card>
    );
  }
  return <LeadView lead={lead.data} />;
}

function LeadView({ lead }: { lead: LeadDetail }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const stages = useQuery(stagesQuery);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const { compose, dialog: composeDialog } = useComposeEmail();
  const openCompose = () => compose(lead);
  const [tab, setTab] = useState("overview");
  const can = useCan();
  const canSend = can("email", "send");
  const { markUnreviewed } = useLeadReview();
  const refreshReviewState = useRefreshReviewState();

  // Opening a lead reviews it. Once per lead; failures only mean it stays bold.
  useEffect(() => {
    if (lead.reviewedByMe) return;
    leadActions.markReviewed(lead.id).then(refreshReviewState, () => {});
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- once per lead opened
  }, [lead.id]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: leadKeys.all });

  const update = useMutation({
    mutationFn: (input: Parameters<typeof leadActions.update>[1]) =>
      leadActions.update(lead.id, input),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const setFollowUp = useMutation({
    mutationFn: (day: Date | null) =>
      leadActions.update(lead.id, { nextFollowUpOn: day ? format(day, "yyyy-MM-dd") : null }),
    onSuccess: async (_, day) => {
      toast.success(
        day ? `Follow-up set for ${format(day, "EEE d MMM yyyy")}.` : "Follow-up cleared.",
      );
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: () => leadActions.remove(lead.id),
    onSuccess: async () => {
      toast.success(`${fullName(lead)} was deleted.`);
      await navigate({ to: "/leads" });
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/leads"
        className="inline-flex w-fit items-center gap-1.5 text-small font-medium text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="size-4" aria-hidden /> All Leads
      </Link>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-h1 text-fg">{fullName(lead)}</h1>
            <span className="text-body text-fg-muted" data-tabular>
              #{lead.leadNumber}
            </span>
          </div>
          <p className="text-body text-fg-secondary">
            {[lead.company, lead.service?.name].filter(Boolean).join(" · ") || "No company given"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <FollowUpBadge day={lead.nextFollowUpOn} />
            <EmailFlagBadge flag={lead.emailFlag} />
          </div>
          <p className="flex items-center gap-2 text-small text-fg-secondary">
            <Users className="size-4 text-fg-muted" aria-hidden />
            {lead.assignees.length ? (
              <>
                <AssigneeAvatars assignees={lead.assignees} />
                <span>
                  Assigned to {lead.assignees.map((person) => person.name).join(", ")}
                </span>
              </>
            ) : (
              <span>Unassigned</span>
            )}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="self-start">
              Actions <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {canSend && (
              <DropdownMenuItem onSelect={openCompose}>
                <Send /> Send email
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setEditOpen(true)}>
              <Pencil /> Edit details
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`mailto:${lead.email}`}>
                <ExternalLink /> Open in my email app
              </a>
            </DropdownMenuItem>
            {can("lead", "assign") && (
              <DropdownMenuItem onSelect={() => setAssignOpen(true)}>
                <UserPlus /> Assign…
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() =>
                markUnreviewed.mutate(lead.id, {
                  // Staying here would count as opening it again.
                  onSuccess: () => {
                    toast.success(`${fullName(lead)} is marked as unreviewed.`);
                    void navigate({ to: "/leads" });
                  },
                })
              }
            >
              <EyeOff /> Mark as unreviewed
            </DropdownMenuItem>
            {can("lead", "delete") && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                  <Trash2 /> Delete lead
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <Card padding="sm" className="grid gap-4 sm:grid-cols-3">
        <Field label="Stage">
          <Select value={lead.stage.id} onValueChange={(stageId) => update.mutate({ stageId })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(stages.data?.stages ?? [{ ...lead.stage, isDefault: false, leadCount: 0 }]).map(
                (stage) => (
                  <SelectItem key={stage.id} value={stage.id}>
                    <span className="flex items-center gap-2">
                      <StageDot color={stage.color as StageColor} /> {stage.name}
                    </span>
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Follow-up">
          <DatePicker
            value={lead.nextFollowUpOn ? parseISO(lead.nextFollowUpOn) : null}
            onChange={(date) => setFollowUp.mutate(date)}
            placeholder="No follow-up set"
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-label text-fg-secondary">Priority</span>
          <p className="flex h-10 items-center text-h2 text-fg" data-tabular>
            {lead.priority}
          </p>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Lead sections">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="notes">
            Notes{lead.counts.notes ? ` (${lead.counts.notes})` : ""}
          </TabsTrigger>
          <TabsTrigger value="emails">Emails</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <Overview lead={lead} />
        </TabsContent>
        <TabsContent value="activity">
          <Card>
            <ActivityTimeline leadId={lead.id} />
          </Card>
        </TabsContent>
        <TabsContent value="notes">
          <Card>
            <NotesPanel leadId={lead.id} canModerate={user.role === "super_admin"} />
          </Card>
        </TabsContent>
        <TabsContent value="emails">
          <Card padding="none">
            <LeadEmailsPanel leadId={lead.id} canSend={canSend} onCompose={openCompose} />
          </Card>
        </TabsContent>
      </Tabs>

      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} />
      <AssignLeadDialog lead={assignOpen ? lead : null} onOpenChange={setAssignOpen} />
      {composeDialog}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${fullName(lead)}?`}
        description="The lead disappears from Purlink. Their emails stay in the email history."
        confirmLabel="Delete lead"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-caption text-fg-muted">{label}</dt>
      {/* wrap-anywhere (not break-words) so long URLs and click IDs can't widen the grid on phones. */}
      <dd className="text-body wrap-anywhere text-fg">
        {children ?? <span className="text-fg-muted">—</span>}
      </dd>
    </div>
  );
}

function Overview({ lead }: { lead: LeadDetail }) {
  const touch = lead.attribution.lastTouch;
  const first = lead.attribution.firstTouch;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="size-4 text-fg-muted" aria-hidden /> Contact
          </CardTitle>
        </CardHeader>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Detail label="Email">
            <a className="text-primary hover:underline" href={`mailto:${lead.email}`}>
              {lead.email}
            </a>
          </Detail>
          <Detail label="Phone">
            <a className="text-primary hover:underline" href={`tel:${lead.phone}`}>
              {formatPhone(lead.phone)}
            </a>
          </Detail>
        </dl>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="size-4 text-fg-muted" aria-hidden /> Enquiry
          </CardTitle>
        </CardHeader>
        <dl className="grid gap-3">
          <Detail label="Service">
            {lead.service
              ? `${lead.service.name}${lead.service.isActive ? "" : " (no longer offered on the form)"}`
              : null}
          </Detail>
          <Detail label="Message">
            {lead.message ? <span className="whitespace-pre-wrap">{lead.message}</span> : null}
          </Detail>
        </dl>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="size-4 text-fg-muted" aria-hidden /> How they found us
          </CardTitle>
        </CardHeader>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Detail label="Channel">{channelLabel(lead.attribution.channel)}</Detail>
          <Detail label="Campaign tag">{lead.attribution.utmCampaign}</Detail>
          <Detail label="First visit source">
            {first?.utm_source
              ? `${first.utm_source}${first.utm_medium ? ` / ${first.utm_medium}` : ""}`
              : null}
          </Detail>
          <Detail label="Latest visit source">
            {touch?.utm_source
              ? `${touch.utm_source}${touch.utm_medium ? ` / ${touch.utm_medium}` : ""}`
              : null}
          </Detail>
          <Detail label="Landing page">
            {touch?.landingPage ? <span className="text-small">{touch.landingPage}</span> : null}
          </Detail>
          <Detail label="Referrer">
            {touch?.referrer ? <span className="text-small">{touch.referrer}</span> : null}
          </Detail>
          {(touch?.gclid || touch?.fbclid || touch?.msclkid) && (
            <Detail label="Ad click ID">{touch.gclid ?? touch.fbclid ?? touch.msclkid}</Detail>
          )}
          <Detail label="Form submissions">{lead.attribution.submissions}</Detail>
        </dl>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tags className="size-4 text-fg-muted" aria-hidden /> Campaign
          </CardTitle>
        </CardHeader>
        {lead.campaign ? (
          <dl className="grid gap-3 sm:grid-cols-2">
            <Detail label="Campaign">
              <Link
                to="/leads"
                search={{ campaignId: lead.campaign.id }}
                className="text-primary hover:underline"
              >
                {lead.campaign.name ?? "Untitled campaign"}
              </Link>
            </Detail>
            <Detail label="Source / medium">
              {[lead.campaign.source, lead.campaign.medium].filter(Boolean).join(" / ") || null}
            </Detail>
            <Detail label="Keywords">
              {lead.campaign.keywords.length ? (
                <span className="flex flex-wrap gap-1">
                  {lead.campaign.keywords.map((keyword) => (
                    <Badge key={keyword}>{keyword}</Badge>
                  ))}
                </span>
              ) : null}
            </Detail>
            <Detail label="Ad variant">{lead.campaign.adVariant}</Detail>
          </dl>
        ) : (
          <p className="text-body text-fg-secondary">
            Not from a tracked campaign. Links made with the campaign link builder are tracked
            automatically.
          </p>
        )}
      </Card>

      {lead.reviewers && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-4 text-fg-muted" aria-hidden /> Reviewed by
            </CardTitle>
          </CardHeader>
          <p className="text-body text-fg">
            {lead.reviewers.length
              ? lead.reviewers.map((person) => person.name).join(", ")
              : "Nobody has opened this lead yet."}
          </p>
          <p className="mt-1 text-caption text-fg-muted">Only Super Admins can see this.</p>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-fg-muted" aria-hidden /> Marketing consent
          </CardTitle>
        </CardHeader>
        {lead.marketingConsent ? (
          <Alert tone="success" title="Agreed to marketing emails">
            {lead.marketingConsentAt ? `On ${formatDateTime(lead.marketingConsentAt)}` : null}
            {lead.consentTextVersion ? ` · wording ${lead.consentTextVersion}` : null}
          </Alert>
        ) : (
          <p className="text-body text-fg-secondary">
            Hasn't agreed to marketing emails. You can still reply to their enquiry.
          </p>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Record</CardTitle>
        </CardHeader>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Detail label="Lead number">#{lead.leadNumber}</Detail>
          <Detail label="Came in via">
            {lead.createdVia === "manual" ? "Added by the team" : "Website form"}
          </Detail>
          <Detail label="Received">{formatDateTime(lead.createdAt)}</Detail>
          <Detail label="Last updated">{formatDate(lead.updatedAt)}</Detail>
          <Detail label="Last contacted">
            {lead.lastContactedAt ? formatDate(lead.lastContactedAt) : "Not yet"}
          </Detail>
        </dl>
      </Card>
    </div>
  );
}
